
import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { AuthForm } from './auth/AuthForm';
import { Briefcase, FileText, Linkedin, GraduationCap } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { useCareerChat, MessageType } from '@/hooks/useCareerChat';
import ChatTab from './career-chat/ChatTab';
import ResumeAnalysisTab from './career-chat/ResumeAnalysisTab';
import LinkedinAnalysisTab from './career-chat/LinkedinAnalysisTab';
import StudentGuidanceTab from './career-chat/StudentGuidanceTab';
import { useIsMobile } from '@/hooks/use-mobile';

const CareerChatBot: React.FC = () => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const { 
    messages, 
    isTyping, 
    isAuthOpen, 
    activeTab, 
    defaultAgent,
    isLoading,
    setIsAuthOpen, 
    handleSendMessage, 
    handleTabChange, 
    handleAuthSuccess 
  } = useCareerChat();

  // Handle tab activation from route state
  useEffect(() => {
    if (location.state?.activeTab && ['general', 'resume', 'linkedin', 'highschool'].includes(location.state.activeTab)) {
      handleTabChange(location.state.activeTab);
    }
  }, [location.state, handleTabChange]);

  const handleSpecializedContentSubmit = (content: string, type: MessageType) => {
    handleSendMessage(content, type);
    handleTabChange('general');
  };

  if (isLoading) {
    return (
      <div className={`${isMobile ? 'w-full p-0' : 'container mx-auto py-8 px-4'}`}>
        <Card className="w-full max-w-3xl mx-auto shadow-soft overflow-hidden border-0 glass-card h-[750px] flex flex-col">
          <CardHeader className="bg-primary/5 border-b">
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              AI Career Agent
            </CardTitle>
            <CardDescription>
              Loading your AI agent...
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div className={`${isMobile ? 'w-full p-0' : 'container mx-auto py-8 px-4'}`}>
        <Card className="w-full max-w-3xl mx-auto shadow-soft overflow-hidden border-0 glass-card h-[750px] flex flex-col">
          <CardHeader className="bg-primary/5 border-b">
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              {defaultAgent?.name || "AI Career Agent"}
            </CardTitle>
            <CardDescription>
              {defaultAgent?.expertise || "Get personalized guidance on career paths, skills, and opportunities"}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="p-0 flex-1 overflow-hidden">
            <Tabs 
              value={activeTab} 
              onValueChange={handleTabChange} 
              className="h-full flex flex-col"
            >
              <TabsList className="grid grid-cols-4 max-w-full">
                <TabsTrigger value="general" className="flex items-center gap-1">
                  <Briefcase className="h-4 w-4" />
                  <span className="hidden sm:inline">Chat</span>
                </TabsTrigger>
                <TabsTrigger value="resume" className="flex items-center gap-1">
                  <FileText className="h-4 w-4" />
                  <span className="hidden sm:inline">Resume</span>
                </TabsTrigger>
                <TabsTrigger value="linkedin" className="flex items-center gap-1">
                  <Linkedin className="h-4 w-4" />
                  <span className="hidden sm:inline">LinkedIn</span>
                </TabsTrigger>
                <TabsTrigger value="highschool" className="flex items-center gap-1">
                  <GraduationCap className="h-4 w-4" />
                  <span className="hidden sm:inline">Student</span>
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="general" className="flex-1 overflow-hidden m-0 border-none p-0">
                <ChatTab 
                  messages={messages}
                  isTyping={isTyping}
                  onSendMessage={(message) => handleSendMessage(message, 'general')}
                  agent={defaultAgent}
                />
              </TabsContent>
              
              <TabsContent value="resume" className="flex-1 h-full m-0 pt-2 p-4 border-none overflow-auto">
                <ResumeAnalysisTab onSubmit={handleSpecializedContentSubmit} />
              </TabsContent>
              
              <TabsContent value="linkedin" className="flex-1 h-full m-0 pt-2 p-4 border-none overflow-auto">
                <LinkedinAnalysisTab onSubmit={handleSpecializedContentSubmit} />
              </TabsContent>
              
              <TabsContent value="highschool" className="flex-1 h-full m-0 pt-2 p-4 border-none overflow-auto">
                <StudentGuidanceTab onSubmit={handleSpecializedContentSubmit} />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        
        <AuthForm 
          open={isAuthOpen} 
          onOpenChange={setIsAuthOpen} 
          onSuccess={handleAuthSuccess}
        />
      </div>
    </div>
  );
};

export default CareerChatBot;
